import React, { Fragment, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { injectIntl } from 'react-intl';
import { ShortSubscriptions } from '../../../../../../utils/user-subscription';
import {
  getHotTopicItemImageUrl,
  getHotTopicItemShareUrl,
  getHotTopicItemPreviewUrl,
  getHotTopicSocialPicZipUrl
} from '../../../../../../utils/hot-topic';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import GetAppIcon from '@mui/icons-material/GetApp';
import LocalMallRoundedIcon from '@mui/icons-material/LocalMallRounded';
import VideoPlayer from '../../../../../../components/VideoPlayer/VideoPlayer';
import { sanitize } from 'dompurify';
import { interpolateString } from '../../../../../../utils/string';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ZoomIn from '@mui/icons-material/ZoomIn';
import { Typography } from '@mui/material';
import moment from 'moment-timezone';
import ShareButtonGroup from '../../../../../../components/ShareButtonGroup/ShareButtonGroup';
import { toggleDialog } from '../../../../../../store/control/duck';
import { tracking } from '../../../../../../utils/tracking';
import _ from 'lodash';
import LanguageRadioGroup from '../../../../../../components/LanguageRadioGroup/LanguageRadioGroup';
import { LANGUAGE } from '../../../../../../constants/language';
import { trackDirect } from '../../../../../../store/analytics/api';
import NativeOrWeb from '../../../../../../utils/native';
import { toast } from 'react-toastify';
import IconToast from '../../../../../../components/NewToast';
import { VideoCardTags } from '../../../../../../components/VideoCard/components/VideoCardTags';
import { updateUserDetails } from '../../../../../../store/user/duck';

const ContentGridContainerStyle: React.CSSProperties = {
  position: 'relative',
  justifyContent: 'space-between',
  margin: '10px 0',
  width: '100%'
};

const TitleStyle: React.CSSProperties = {
  maxWidth: '60%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between'
};

const PreviewButtonStyle = (isExcluded: boolean): React.CSSProperties =>
  isExcluded
    ? {
        color: '#FFFFFF',
        backgroundColor: '#4F1271',
        width: '130px'
      }
    : {
        width: '130px'
      };

const ImageStyle: React.CSSProperties = {
  width: '130px',
  height: '195px',
  margin: '0 0 10px 0'
};

const ThumbNailStyle: React.CSSProperties = {
  textAlign: 'center'
};

const HotTopicItemDialogContent: React.FC<any> = props => {
  const {
    intl,
    history,
    userDetails,
    updateUserDetails,
    subscriptions,
    item,
    enableLanguages,
    onClickSuggestion,
    toggleDialog,
    toggleDialogShowTags
  } = props;

  const [locale, setLocale] = useState(
    history.location.state?.language
      ? history.location.state.language === 'Mandarin'
        ? LANGUAGE.ZH
        : LANGUAGE.ZH_HANT_HK
      : intl.locale === LANGUAGE.EN
      ? LANGUAGE.ZH_HANT_HK
      : intl.locale
  );
  const [isIpadWidth, setIsIpadWidth] = useState(window.innerWidth > 767);
  const dispatch = useDispatch();

  const {
    id,
    piktoId,
    title,
    texts,
    thumbnail,
    graphicLink,
    videoId,
    pdfLink,
    suggestions,
    excludeFreeTrial,
    excludePayTrial,
    updatedAt,
    createdAt,
    socialPicZip,
    tags
  } = item || {};

  const isItemFreeTrialExcluded =
    excludeFreeTrial && new ShortSubscriptions(subscriptions).isCoreSubscriptionFreeTrial();
  const isItemPayTrialExcluded = excludePayTrial && new ShortSubscriptions(subscriptions).isCoreSubscriptionTrialing();
  const isExcluded = isItemFreeTrialExcluded || isItemPayTrialExcluded;

  // compatible with piktoId = 'xxx' format (can remove later)
  const finalPiktoId = piktoId ? piktoId[locale] || piktoId : '';

  const externalPiktoLinkWithoutSearch = getHotTopicItemShareUrl(item, locale);
  const externalPiktoLink = getHotTopicItemShareUrl(item, locale, { aId: _.get(userDetails, '_id', '') });
  const externalPreviewPiktoLink = getHotTopicItemPreviewUrl(item, locale, { aId: _.get(userDetails, '_id', '') });

  const globalVariables = { externalPiktoLinkWithoutSearch, externalPiktoLink };

  const thumbnailImageUrl = getHotTopicItemImageUrl(item);

  const download = () => {
    if (isExcluded) {
      toggleDialog('freeTrialLazyPackExcluded', {});
    } else {
      window.open(pdfLink);
      tracking('Adviser download in help center', { id: id });
    }
  };

  const pikto = () => {
    if (isExcluded) {
      toggleDialog('freeTrialLazyPackExcluded', {});
    } else {
      tracking('Click on Hot Topic - Preview');
      trackDirect('previewHotTopic', id.replace('featured-', ''));
      window.open(externalPreviewPiktoLink, '_blank');
    }
  };

  const isFavourite =
    id && userDetails.favouriteHotTopics && userDetails.favouriteHotTopics.includes(id.replace('featured-', ''));
  const changeFavourite = () => {
    if (isFavourite) {
      dispatch(
        updateUserDetails({ favouriteHotTopics: { $pull: { favouriteHotTopics: id.replace('featured-', '') } } })
      );
      toast.info(IconToast('success', intl.formatMessage({ id: 'removed-hot-topic-from-favourite' })), {
        className: 'new-toast'
      });
    } else {
      dispatch(
        updateUserDetails({ favouriteHotTopics: { $addToSet: { favouriteHotTopics: id.replace('featured-', '') } } })
      );
      toast.info(IconToast('success', intl.formatMessage({ id: 'added-hot-topic-to-favourite' })), {
        className: 'new-toast'
      });
    }
  };

  const downloadSocialPicZip = async () => {
    if (isExcluded) {
      toggleDialog('freeTrialLazyPackExcluded', {});
    } else {
      try {
        const url = getHotTopicSocialPicZipUrl(socialPicZip);

        const zipBlob = await fetch(url).then(r => r.blob());

        await NativeOrWeb.downloadZipFile(url, `${socialPicZip}_${moment().format('DDMMMYYYY')}.zip`, zipBlob);

        tracking('Click on Hot Topic - Download Social Media Pack');

        trackDirect('downloadHotTopicSocialPic', id.replace('featured-', ''));
      } catch (error) {
        console.error('Error downloading social media pack:', error);
        toast.info(IconToast('error', intl.formatMessage({ id: 'Something went wrong' })), { className: 'new-toast' });
      }
    }
  };

  const onClickGraphicLink = async () => {
    toggleDialog('iframe', { title: intl.formatMessage({ id: 'click-graphic-link' }), url: graphicLink });
  };

  const shop = () => history.push('/purchase-lazy-pack');

  const onChangeLocale = (event: any) => setLocale(event.target.value);

  useEffect(() => {
    const handleResize = () => {
      setIsIpadWidth(window.innerWidth > 767);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Grid container spacing={1}>
      {videoId && !!videoId.trim() && (
        <Grid size={12}>
          <VideoPlayer url={videoId} />
        </Grid>
      )}
      <Grid size={12}>
        <Grid container sx={{ flexDirection: 'column' }}>
          {toggleDialogShowTags && (
            <Grid>
              <VideoCardTags
                locale={locale}
                tags={tags}
                usePlaceholder={false}
                color={undefined}
                backgroundColor={undefined}
              />
            </Grid>
          )}
          <Grid>
            <div>
              {texts?.[intl.locale].map((text: any, index: number) => (
                <div key={index} className="text-wrapper">
                  <Grid container direction="row" style={ContentGridContainerStyle}>
                    <Grid style={isIpadWidth ? TitleStyle : {}}>
                      <div
                        className="hot-topic-text-title"
                        dangerouslySetInnerHTML={{ __html: sanitize(text.subtitle || '') }}
                      />
                      <div
                        className="text-description"
                        dangerouslySetInnerHTML={{
                          __html: sanitize(interpolateString(text.description, globalVariables) || '')
                        }}
                      />

                      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
                        {!isIpadWidth && finalPiktoId && (
                          <Grid>
                            <Button
                              variant="outlined"
                              color="primary"
                              style={PreviewButtonStyle(isExcluded)}
                              startIcon={isExcluded ? <LockOutlinedIcon /> : <ZoomIn />}
                              onClick={pikto}
                            >
                              {intl.formatMessage({ id: 'preview' })}
                            </Button>
                          </Grid>
                        )}
                        {!isIpadWidth && socialPicZip && (
                          <Grid style={{ marginLeft: 8 }}>
                            <Button
                              variant="contained"
                              color="primary"
                              style={PreviewButtonStyle(isExcluded)}
                              startIcon={isExcluded ? <LockOutlinedIcon /> : <GetAppIcon />}
                              onClick={downloadSocialPicZip}
                            >
                              {intl.formatMessage({ id: 'social-pic' })}
                            </Button>
                          </Grid>
                        )}
                        {!isIpadWidth && (
                          <Grid style={{ marginLeft: 10 }}>
                            {isFavourite ? (
                              <img src="img/pin_selected.svg" onClick={changeFavourite} style={{ width: 25 }} />
                            ) : (
                              <img src="img/pin_unselected.svg" onClick={changeFavourite} style={{ width: 25 }} />
                            )}
                          </Grid>
                        )}
                      </div>
                    </Grid>
                    {isIpadWidth && thumbnail && (
                      <Grid style={ThumbNailStyle}>
                        <Box style={{ display: 'flex', justifyContent: 'end', marginBottom: 4 }}>
                          {isFavourite ? (
                            <img
                              src="img/pin_selected.svg"
                              onClick={changeFavourite}
                              style={{ width: 25, cursor: 'pointer', marginBottom: 6 }}
                            />
                          ) : (
                            <img
                              src="img/pin_unselected.svg"
                              onClick={changeFavourite}
                              style={{ width: 25, cursor: 'pointer', marginBottom: 6 }}
                            />
                          )}
                        </Box>
                        <img style={ImageStyle} src={thumbnailImageUrl} alt="thumbnail" />
                        {finalPiktoId && (
                          <Grid style={{ marginTop: 5 }}>
                            <Button
                              variant="outlined"
                              color="primary"
                              style={PreviewButtonStyle(isExcluded)}
                              startIcon={isExcluded ? <LockOutlinedIcon /> : <ZoomIn />}
                              onClick={pikto}
                            >
                              {intl.formatMessage({ id: 'preview' })}
                            </Button>
                          </Grid>
                        )}
                        {socialPicZip && (
                          <Grid style={{ marginTop: 5 }}>
                            <Button
                              variant="contained"
                              color="primary"
                              style={PreviewButtonStyle(isExcluded)}
                              startIcon={isExcluded ? <LockOutlinedIcon /> : <GetAppIcon />}
                              onClick={downloadSocialPicZip}
                            >
                              {intl.formatMessage({ id: 'social-pic' })}
                            </Button>
                          </Grid>
                        )}
                      </Grid>
                    )}
                  </Grid>
                  {suggestions && suggestions.length > 0 && (
                    <Grid container style={{ margin: '20px 0 10px 0' }} sx={{ flexDirection: 'column' }}>
                      <Grid>
                        <Typography>{intl.formatMessage({ id: 'hot-topic-suggestions' })}</Typography>
                      </Grid>
                      {suggestions.map((suggestion: any) => {
                        const onClick = (event: any) => {
                          event.preventDefault();
                          onClickSuggestion(suggestion);
                        };
                        return (
                          <Grid key={suggestion.id}>
                            <a
                              href={`${window.location.pathname}#${suggestion.id}`}
                              style={{
                                color: '#3966F8',
                                textDecoration: 'underline'
                              }}
                              onClick={onClick}
                            >
                              <Typography>{suggestion.title[intl.locale]}</Typography>
                            </a>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                  {(updatedAt || createdAt) && (
                    <Typography color="textSecondary" gutterBottom={true}>
                      {`${intl.formatMessage({ id: 'Last Update' })}: ${moment(updatedAt || createdAt).format(
                        'D-MMM-YYYY'
                      )}`}
                    </Typography>
                  )}
                </div>
              ))}

              {enableLanguages && (
                <LanguageRadioGroup
                  options={[LANGUAGE.ZH_HANT_HK, LANGUAGE.ZH]}
                  value={locale}
                  onChange={onChangeLocale}
                />
              )}

              {texts?.[locale].map((text: any, index: number) => (
                <div key={index} className="text-wrapper">
                  {text['copy-text'] &&
                    text['copy-text'].length > 0 &&
                    text['copy-text'].map((t: any, copyTextIndex: number) => {
                      t = interpolateString(t, globalVariables);

                      const externalPiktoLinkWithoutFeature = externalPiktoLink.replace('featured-', '');
                      return (
                        <div className="text-copy" key={copyTextIndex}>
                          <ShareButtonGroup
                            key={id}
                            title={
                              history.location.state?.share_message
                                ? history.location.state.share_message
                                : t.replace(/<br\s*[\/]?>/gi, '\n').replace(/<\/?[^>]+(>|$)/g, '')
                            }
                            wechatTitle={title?.zh || title?.[intl.locale]}
                            wechatThumbnail={thumbnailImageUrl}
                            url={externalPiktoLinkWithoutFeature}
                            isCTA
                            locked={isExcluded ? () => toggleDialog('freeTrialLazyPackExcluded', {}) : null}
                            onShare={() => {
                              tracking('Click on Hot Topic - Native share');
                              trackDirect('shareHotTopic', id.replace('featured-', ''));
                            }}
                            onWhatsapp={() => {
                              tracking('Click on Hot Topic - Whatsapp Share');
                              trackDirect('shareHotTopic', id.replace('featured-', ''));
                            }}
                            onWechat={() => {
                              tracking('Click on Hot Topic - Wechat Share');
                              trackDirect('shareHotTopic', id.replace('featured-', ''));
                            }}
                            onCopy={() => {
                              tracking('Click on Hot Topic - Copy');
                              trackDirect('shareHotTopic', id.replace('featured-', ''));
                            }}
                            CopyTextFieldProps={{
                              multiline: true,
                              rows: 2
                            }}
                          />
                        </div>
                      );
                    })}
                </div>
              ))}
            </div>
          </Grid>
          <Grid>
            <div>
              {graphicLink && (
                <Grid container spacing={1}>
                  <Grid>
                    <Button variant="outlined" color="primary" onClick={onClickGraphicLink} className="basic-button">
                      {intl.formatMessage({ id: 'click-graphic-link' })}
                    </Button>
                  </Grid>
                </Grid>
              )}
              <Grid container spacing={1}>
                {pdfLink && !!pdfLink.trim() && (
                  <Fragment>
                    <Grid>
                      <Button variant="contained" color="primary" onClick={download}>
                        {/* {intl.formatMessage({ id: 'download-pdf' })} */}
                        <GetAppIcon />
                      </Button>
                    </Grid>
                    {isExcluded && (
                      <Grid>
                        <Button variant="contained" color="primary" onClick={shop}>
                          <LocalMallRoundedIcon />
                        </Button>
                      </Grid>
                    )}
                  </Fragment>
                )}
              </Grid>
            </div>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const container = connect(
  (state: any) => ({
    userDetails: state.user.userDetails,
    subscriptions: state.userSubscription.subscriptions
  }),
  {
    toggleDialog,
    updateUserDetails
  }
)(HotTopicItemDialogContent);

export default injectIntl(container);
